import * as React from 'react';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import LaunchIcon from '@mui/icons-material/LaunchOutlined';
import { Box, Button, Link, SwipeableDrawer, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import CloseSidebarIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import OpenSidebarIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { NavLink } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { NavView } from './NavView';
import { useNavSections } from '../utils/hooks';
import { useTranslation } from 'react-i18next';
import { atom, useRecoilState } from 'recoil';
import { useIsAdmin, userState } from '../atoms';
import { headerHeight } from '../Constants';

const drawerWidth = 200;
const breakpoint = 800;

const useStyle = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        [theme.breakpoints?.down(breakpoint)]: {
            marginLeft: 0
        }
    },
    appBar: {
        backgroundColor: '#fff',
        color: 'rgba(0, 0, 0, 0.7)',
        height: '54px',
        transition: theme.transitions?.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
    },
    drawerPaper: {
        width: drawerWidth,
    },
    hide: {
        display: 'none',
    },
    logoBar: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
    },
    logo: {
        marginLeft: 0,
        [theme.breakpoints?.up(breakpoint)]: {
            marginLeft: '-8px',
        }
    },
    nav: {
        flex: 1,
        '&:hover': {
            overflow: 'auto'
        }
    },
    title: {
        fontWeight: 500,
        fontSize: '1.5rem'
    },
    version: {
        margin: '0.5rem 1rem'
    },
    content: {
        overflow: 'auto',
        height: 'calc(100vh - 64px)'
    }
}));

const OpenSidebarSection = ({ onOpen }: { onOpen: () => void }) => {
    return (
        <div className='open-sidebar'>
            <Button variant='text' sx={{ padding: 0, minWidth: 0 }} onClick={onOpen}>
                <OpenSidebarIcon fontSize='large' color="primary"/>
            </Button>
        </div>
    )
}
const CloseSidebarSection = ({ onClose }: { onClose: () => void }) => {
    return (
        <div className='close-sidebar'>
            <Button variant='text' sx={{ padding: 0, minWidth: 0 }} onClick={onClose}>
                <CloseSidebarIcon fontSize='large' color="secondary"/>
            </Button>
        </div>
    )
}

type MyNavLinkPropsType = {
    to: string,
    label?: string,
    target?: React.HTMLAttributeAnchorTarget,
    icon?: React.ReactNode
}

export const MyNavLink: React.FC<MyNavLinkPropsType> = ({ to, label, target, icon }) => {
    return (
        <>
            <MyInternalNavLink to={to} target={target}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: grey[800], padding: 0.5 }}>
                    {icon && <Box sx={theme => ({ color: theme.palette.primary.main, display: 'inline' })}>{icon}</Box>}{label && <span className='ms-1 mylink' style={{color: '#1d2d3d'}}>{label}</span>}
                </Box>
            </MyInternalNavLink>
        </>
    )
}
const MyInternalNavLink: React.FC<{ to: string, target?: React.HTMLAttributeAnchorTarget, icon?: React.ReactNode, children?: React.ReactNode }> = ({ to, target, children }) => {
    return (to.startsWith("http")
        ? <Link underline='none' href={to} target={target}>{children}</Link>
        : <NavLink className='mylink' to={to} target={target}>{children}</NavLink>
    )
}

type SidebarSectionPropsType = {
    initialOpen: boolean;
}

const sidebarOpenLocalstorageKey = (username: string) => `sidebar-open-${username}`

const saveSidebarOpenState = (username: string, open: boolean) => {
    window.localStorage.setItem(sidebarOpenLocalstorageKey(username), JSON.stringify({ open }))
}
const getInitialSidebarOpenState = (username: string) => {
    let json = window.localStorage.getItem(sidebarOpenLocalstorageKey(username))
    if(!json) {
        json =  '{ "open": true }'
        saveSidebarOpenState(username, true)
    }
    const temp: { open: boolean } = JSON.parse(json)
    return temp.open
}

const sidebarOpenState = atom({
    key: 'sidebarOpenState',
    default: true,
    effects: [
        ({ setSelf, getLoadable }) => {
            const user = getLoadable(userState).valueMaybe()
            const username = user?.name ?? 'Unknown'
            setSelf(() => getInitialSidebarOpenState(username))
        },
        ({ onSet, getLoadable }) => {
            const user = getLoadable(userState).valueMaybe()
            const username = user?.name ?? 'Unknown'
            onSet((newValue) => saveSidebarOpenState(username, newValue))
        },
    ]
})

export const SidebarSection: React.FC<SidebarSectionPropsType> = ({ initialOpen }) => {
    const isAdmin = useIsAdmin()
    const [open, setOpen] = useRecoilState(sidebarOpenState)
    const onClose = () => setOpen(false)
    const onOpen = () => setOpen(true)
    const theme = useTheme();
    const classes = useStyle();
    const mobile = useMediaQuery(theme.breakpoints.down(breakpoint));
    const navSections = useNavSections();
    const { t } = useTranslation();

    // console.log({isAdmin})
    return (
        <>
            {open
                ?
                <>
                    <SwipeableDrawer
                        id='drawer'
                        variant={!mobile ? 'persistent' : 'temporary'}
                        open={open}
                        className={classes.drawer}
                        anchor="left"
                        onOpen={onOpen}
                        onClose={onClose}
                        classes={{ paper: classes.drawerPaper }}
                    >
                        <Box sx={{ mt: `${headerHeight}px`, height: 40 }} className='sidebar-section'>
                            <CloseSidebarSection onClose={onClose} />
                        </Box>
                        <div className={classes.nav}>
                            <NavView sections={navSections} />
                        </div>
                        <div className='separator'>&nbsp;</div>
                        <MyNavLink to="/" label="Home" icon={<HomeIcon color='primary'/>} />
                        <MyNavLink to="/help" label="Help" icon={<HelpIcon color='primary'/>} />
                        <MyNavLink to="https://servizi.sixtemacloud.it" target="_blank" label={t('Portale classico')} icon={<LaunchIcon color='primary'/>} />
                        <Typography variant="caption" className={classes.version}>{`vers. ${process.env.REACT_APP_VERSION}`}</Typography>
                    </SwipeableDrawer>
                </>
                : <Box sx={{ boxShadow: 2 }} className='sidebar-section'>
                    <Box sx={{ml: 1}}>
                        <OpenSidebarSection onOpen={onOpen} />
                    </Box>
                    <NavView sections={navSections} compressed />
                    <div className='separator-closed'>&nbsp;</div>
                    <Box sx={{ml: 1}}>
                        <MyNavLink to="/" icon={<HomeIcon color='primary'/>} />
                        <MyNavLink to="/help" icon={<HelpIcon color='primary'/>} />
                        <MyNavLink to="https://servizi.sixtemacloud.it" target="_blank" icon={<LaunchIcon color='primary'/>} />
                    </Box>
                    <Typography variant="caption" className={classes.version}>&nbsp;</Typography>
                </Box>
            }
        </>
    );
};
